import { useEffect, useState, type ReactNode } from "react";
import { Link } from "react-router-dom";
import { Check, Copy, KeyRound } from "lucide-react";

import { usarConfiguracion, usarGuardarConfiguracion, usarLibro, usarRecibidos, usarTelegram } from "../ganchos/datos.ts";
import { haceRato, plural } from "../formato.ts";
import { codigoGmail, pasosHechos, ultimoCorreo } from "./empezar-modelo.ts";
import { Boton, Seccion, cx } from "../componentes/ui.tsx";

/**
 * Empezar sin ayuda (SPEC-029 §15).
 *
 * Cinco pasos, en el orden en que se hacen. Cada uno dice si ya está hecho
 * mirando lo que de verdad pasó —un correo que entró, un Telegram vinculado—,
 * no una casilla que alguien marca. Así la página sirve también para
 * diagnosticar: «no me llegan los pagos» se responde aquí.
 */
/**
 * Cuántos pasos hay hechos, mirando lo que de verdad pasó. Lo usan esta página
 * y el aviso del Resumen. `vivo` hace que los recibidos se refresquen solos
 * mientras se espera la prueba. `activo` en falso no pide nada: el menú la usa
 * para todos los roles, y solo quien configura puede leer la configuración.
 */
export function usarPasosDeInicio(vivo = false, activo = true) {
  const config = usarConfiguracion(activo);
  const libro = usarLibro();
  const telegram = usarTelegram(activo);
  const recibidos = usarRecibidos("", false, vivo, activo);
  const correos = recibidos.data?.correos ?? [];
  const reenviadores = config.data?.claves.find((c) => c.clave === "RECAUDA_REENVIADORES")?.valor ?? "";
  const hechos = pasosHechos({
    direccion: config.data?.direccionEntrada ?? null,
    reenviadores,
    correos,
    telegramVinculado: !!telegram.data?.vinculo,
    facturas: libro.data?.facturas?.length ?? 0,
  });
  const listo = !config.isPending && !recibidos.isPending && !telegram.isPending && !libro.isPending;
  return {
    hechos,
    cuantos: Object.values(hechos).filter(Boolean).length,
    listo,
    direccion: config.data?.direccionEntrada ?? null,
    reenviadores,
    correos,
    facturas: libro.data?.facturas?.length ?? 0,
  };
}

export function Empezar() {
  const [esperando, setEsperando] = useState(true);
  const { hechos, cuantos, direccion, reenviadores, correos, facturas } = usarPasosDeInicio(esperando);
  useEffect(() => setEsperando(!hechos.prueba), [hechos.prueba]);

  return (
    <Seccion
      titulo="Empezar con 4drando"
      descripcion={`${cuantos} de 5 pasos hechos. Cuando estén los cinco, cada pago que entre a tu banco te llegará solo.`}
    >
      <ol className="space-y-3">
        <PasoTarjeta n={1} titulo="Tu dirección de 4drando" hecho={hechos.direccion}>
          <Direccion direccion={direccion} />
          <p className="text-sm text-tenue">
            Reenvía aquí las alertas de tu banco. Solo las alertas: el resto de tu correo no lo
            necesitamos.
          </p>
          <Instrucciones direccion={direccion ?? "tu dirección de 4drando"} />
        </PasoTarjeta>

        <PasoTarjeta n={2} titulo="¿Desde qué correo reenvías?" hecho={hechos.reenviador}>
          <Reenviador actual={reenviadores} />
          <CodigoGmail codigo={codigoGmail(correos)} />
        </PasoTarjeta>

        <PasoTarjeta n={3} titulo="Mándate una prueba" hecho={hechos.prueba}>
          <Prueba hecho={hechos.prueba} correos={correos} />
        </PasoTarjeta>

        <PasoTarjeta n={4} titulo="Avisos en tu celular" hecho={hechos.avisos}>
          <p className="text-sm text-tenue">
            {hechos.avisos
              ? "Telegram está conectado: cada pago te avisa en el momento."
              : "Conecta Telegram para que cada pago te avise en el momento, con monto y quién pagó."}
          </p>
          {!hechos.avisos && (
            <Link to="/configuracion#telegram">
              <Boton tamano="sm" variante="suave">
                Conectar Telegram
              </Boton>
            </Link>
          )}
        </PasoTarjeta>

        <PasoTarjeta n={5} titulo="Tu cartera" hecho={hechos.cartera}>
          <p className="text-sm text-tenue">
            {hechos.cartera
              ? `Tienes ${facturas} ${plural(facturas, "factura", "facturas")}: 4drando ya puede decirte contra cuál va cada pago.`
              : "Con tus facturas, 4drando te dice contra cuál va cada pago. Conecta tu sistema o regístralas a mano."}
          </p>
          {!hechos.cartera && (
            <div className="flex flex-wrap gap-2">
              <Link to="/configuracion#credenciales">
                <Boton tamano="sm" variante="suave">
                  Conectar mi sistema
                </Boton>
              </Link>
              <Link to="/registrar">
                <Boton tamano="sm" variante="fantasma">
                  Registrar a mano
                </Boton>
              </Link>
            </div>
          )}
        </PasoTarjeta>
      </ol>
    </Seccion>
  );
}

function PasoTarjeta({ n, titulo, hecho, children }: { n: number; titulo: string; hecho: boolean; children: ReactNode }) {
  return (
    <li className="tarjeta flex gap-4 p-4">
      <span
        className={cx(
          "grid size-7 shrink-0 place-items-center rounded-full text-sm font-semibold",
          hecho ? "bg-exito-suave text-exito-tinta" : "bg-superficie-2 text-tenue",
        )}
        aria-label={hecho ? "Hecho" : "Pendiente"}
      >
        {hecho ? <Check className="size-4" /> : n}
      </span>
      <div className="min-w-0 flex-1 space-y-2.5">
        <p className="font-semibold text-tinta">{titulo}</p>
        {children}
      </div>
    </li>
  );
}

function Direccion({ direccion }: { direccion: string | null }) {
  const [copiado, setCopiado] = useState(false);
  if (!direccion) return <p className="text-sm text-tenue">Cargando…</p>;
  return (
    <div className="flex flex-wrap items-center gap-2">
      <code className="cifra rounded-lg border border-borde bg-superficie-2 px-3 py-1.5 text-[15px] text-tinta select-all">
        {direccion}
      </code>
      <Boton
        tamano="sm"
        variante="suave"
        icono={copiado ? Check : Copy}
        onClick={() => {
          void navigator.clipboard?.writeText(direccion);
          setCopiado(true);
          setTimeout(() => setCopiado(false), 2000);
        }}
      >
        {copiado ? "Copiada" : "Copiar"}
      </Boton>
    </div>
  );
}

function Instrucciones({ direccion }: { direccion: string }) {
  return (
    <details className="text-sm text-tenue">
      <summary className="cursor-pointer text-xs font-medium hover:text-tinta">¿Cómo se reenvía?</summary>
      <div className="mt-2 space-y-3 leading-relaxed">
        <div>
          <p className="font-medium text-tinta">En Gmail</p>
          <ol className="ml-4 list-decimal space-y-0.5">
            <li>Configuración → Ver toda la configuración → Reenvío y correo POP/IMAP.</li>
            <li>
              «Añadir una dirección de reenvío» y escribe <span className="cifra">{direccion}</span>.
            </li>
            <li>Gmail pide un código: aparece en el paso 2 de esta página en cuanto llega.</li>
            <li>
              Crea un filtro con el remitente de las alertas de tu banco y elige «Reenviarlo a» esa
              dirección.
            </li>
          </ol>
        </div>
        <div>
          <p className="font-medium text-tinta">En Outlook</p>
          <ol className="ml-4 list-decimal space-y-0.5">
            <li>Configuración → Correo → Reglas → Agregar nueva regla.</li>
            <li>Condición: «De» el remitente de las alertas de tu banco.</li>
            <li>
              Acción: «Reenviar a» <span className="cifra">{direccion}</span>.
            </li>
          </ol>
        </div>
      </div>
    </details>
  );
}

/**
 * El buzón desde el que se reenvía. Sin él, 4drando rechaza los reenvíos:
 * cualquiera podría mandarle «alertas» a una dirección que es pública.
 */
function Reenviador({ actual }: { actual: string }) {
  const guardar = usarGuardarConfiguracion();
  const [valor, setValor] = useState(actual);
  useEffect(() => setValor(actual), [actual]);
  const cambiado = valor.trim() !== actual.trim();
  return (
    <div className="space-y-1.5">
      <p className="text-sm text-tenue">
        El correo tuyo que reenvía las alertas. Si no está aquí, 4drando rechaza lo que llegue desde él.
      </p>
      <form
        className="flex flex-wrap items-center gap-2"
        onSubmit={(e) => {
          e.preventDefault();
          guardar.mutate({ RECAUDA_REENVIADORES: valor.trim() || null });
        }}
      >
        <input
          type="email"
          multiple
          value={valor}
          onChange={(e) => setValor(e.target.value)}
          placeholder="contabilidad@tuempresa.com"
          className="h-9 min-w-64 flex-1 rounded-lg border border-borde bg-superficie px-3 text-sm placeholder:text-tenue-2 focus:border-marca focus:outline-none"
        />
        <Boton type="submit" tamano="sm" variante="suave" disabled={!cambiado} cargando={guardar.isPending}>
          Guardar
        </Boton>
      </form>
      {guardar.error && <p className="text-xs text-riesgo-tinta">{guardar.error.message}</p>}
    </div>
  );
}

function CodigoGmail({ codigo }: { codigo: { codigo: string; cuando: string } | null }) {
  if (!codigo) return null;
  return (
    <div className="flex flex-wrap items-center gap-3 rounded-xl border border-marca/40 bg-marca-suave/40 p-3 text-sm">
      <KeyRound className="size-4 text-marca" aria-hidden />
      <span className="text-tinta">
        Gmail te pidió un código para confirmar el reenvío ({haceRato(codigo.cuando)}):
      </span>
      <code className="cifra text-lg font-semibold tracking-wider text-tinta select-all">{codigo.codigo}</code>
    </div>
  );
}

function Prueba({ hecho, correos }: { hecho: boolean; correos: Parameters<typeof ultimoCorreo>[0] }) {
  const ultimo = ultimoCorreo(correos);
  if (hecho) {
    return <p className="text-sm text-tenue">Ya entró tu primer correo del banco. Todo funciona.</p>;
  }
  return (
    <div className="space-y-1.5 text-sm text-tenue">
      <p>
        Reenvía a tu dirección de 4drando cualquier alerta de pago de tu banco. Esta página se
        actualiza sola: verás aquí si entró.
      </p>
      {ultimo ? (
        <p className="text-aviso-tinta">
          Lo último que llegó ({haceRato(ultimo.recibido_en)}, de {ultimo.remitente}) no entró:{" "}
          {ultimo.detalle ?? ultimo.motivo ?? "revisa Recibidos para ver por qué"}.{" "}
          <Link to="/recibidos" className="underline">
            Ver en Recibidos
          </Link>
        </p>
      ) : (
        <p>Todavía no ha llegado nada.</p>
      )}
    </div>
  );
}

